import { useCallback, useEffect, useRef, useState } from "react";
import { matchCommand } from "./commands";

/**
 * Hook modulare per l'assistente vocale del TIME Watch — Fase 1.
 *
 * Wrappa l'API Web Speech (SpeechRecognition / webkitSpeechRecognition).
 * Progettato per essere esteso nelle fasi successive (ascolto continuo,
 * intent parsing intelligente, contesto conversazionale, ecc.).
 *
 * Uso:
 *   const { supported, listening, status, feedback, start, stop } =
 *     useVoiceAssistant({ onCommand: (cmd) => { ... } });
 */

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useVoiceAssistant({ onCommand } = {}) {
  const SR = getSpeechRecognition();
  const supported = !!SR;

  const [listening, setListening] = useState(false);
  // 'idle' | 'listening' | 'processing' | 'success' | 'error' | 'unsupported'
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [feedback, setFeedback] = useState("");
  const [lastTranscript, setLastTranscript] = useState("");

  const recognitionRef = useRef(null);
  const onCommandRef = useRef(onCommand);
  const feedbackTimerRef = useRef(null);

  useEffect(() => {
    onCommandRef.current = onCommand;
  }, [onCommand]);

  const clearFeedbackSoon = useCallback((ms = 2600) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setFeedback(""), ms);
  }, []);

  const stop = useCallback(() => {
    const r = recognitionRef.current;
    if (r) {
      try {
        r.stop();
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const handleResults = useCallback(
    (transcripts) => {
      // Prova tutte le alternative del riconoscitore. La prima che matcha vince.
      let chosen = transcripts[0] || "";
      let matchedCmd = null;
      let anyHasWake = false;
      for (const t of transcripts) {
        const res = matchCommand(t);
        if (res.hasWake) anyHasWake = true;
        if (res.matched) {
          chosen = t;
          matchedCmd = res.matched;
          break;
        }
      }
      setLastTranscript(chosen);

      if (matchedCmd) {
        setStatus("success");
        setFeedback(`✓ ${matchedCmd.label}`);
        try {
          if (navigator.vibrate) navigator.vibrate(35);
        } catch (e) {
          /* ignore */
        }
        try {
          onCommandRef.current?.(matchedCmd);
        } catch (e) {
          /* ignore */
        }
        clearFeedbackSoon();
      } else if (!anyHasWake) {
        setStatus("error");
        setFeedback('Riprova: pronuncia "Ora TIME..."');
        clearFeedbackSoon();
      } else {
        setStatus("error");
        setFeedback("Comando non riconosciuto. Prova: Ora TIME, cosa puoi fare.");
        clearFeedbackSoon(3800);
      }
    },
    [clearFeedbackSoon]
  );

  const start = useCallback(() => {
    if (!supported) {
      setStatus("unsupported");
      setFeedback("Riconoscimento vocale non supportato da questo browser.");
      clearFeedbackSoon(3800);
      return;
    }
    // Toggle: se già in ascolto → stop
    if (recognitionRef.current) {
      stop();
      return;
    }

    let recognition;
    try {
      recognition = new SR();
    } catch (e) {
      setStatus("error");
      setFeedback("Impossibile avviare il microfono.");
      clearFeedbackSoon();
      return;
    }
    recognition.lang = "it-IT";
    recognition.interimResults = false;
    recognition.maxAlternatives = 4;
    recognition.continuous = false;

    recognition.onstart = () => {
      setListening(true);
      setStatus("listening");
      setFeedback("");
    };

    recognition.onerror = (event) => {
      setListening(false);
      const err = event?.error || "unknown";
      if (err === "not-allowed" || err === "service-not-allowed") {
        setStatus("error");
        setFeedback("Permesso microfono negato.");
      } else if (err === "no-speech") {
        setStatus("error");
        setFeedback("Non ho sentito nulla. Riprova.");
      } else if (err === "aborted") {
        setStatus("idle");
      } else if (err === "audio-capture") {
        setStatus("error");
        setFeedback("Microfono non disponibile.");
      } else if (err === "network") {
        setStatus("error");
        setFeedback("Errore di rete. Riprova.");
      } else {
        setStatus("error");
        setFeedback("Errore durante l'ascolto.");
      }
      if (err !== "aborted") clearFeedbackSoon();
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      setStatus((s) => (s === "listening" ? "idle" : s));
    };

    recognition.onresult = (event) => {
      setStatus("processing");
      const results = event.results[event.results.length - 1];
      const alts = [];
      for (let i = 0; i < results.length; i++) {
        const alt = results[i];
        if (alt && alt.transcript) alts.push(alt.transcript);
      }
      handleResults(alts);
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
    } catch (e) {
      recognitionRef.current = null;
      setListening(false);
      setStatus("error");
      setFeedback("Impossibile avviare il microfono.");
      clearFeedbackSoon();
    }
  }, [supported, SR, stop, handleResults, clearFeedbackSoon]);

  // Cleanup su unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      try {
        recognitionRef.current?.abort?.();
      } catch (e) {
        /* ignore */
      }
      recognitionRef.current = null;
    };
  }, []);

  return {
    supported,
    listening,
    status,
    feedback,
    lastTranscript,
    start,
    stop,
  };
}
